/* eslint-disable no-param-reassign */
import isEmpty from 'lodash/isEmpty';
import isFunction from 'lodash/isFunction';
import filter from 'lodash/filter';
import each from 'lodash/each';
import reject from 'lodash/reject';

import TreeView from '@Services/TreeView';
import { TAXOMONY_STATES } from '@Services/TreeView/TreeViewConstants';

import {
  taxonomies as taxonomiesSelector,
  initialConfig as initialConfigSelector,
  selectionOrder as selectionOrderSelector,
} from './TreeViewSelectors';

import {
  treeViewSetTaxonomiesReducerType,
  treeViewSetSelectedTaxonomiesReducerType,
  treeViewSetSelectedListExpandedReducerType,
  treeViewSetInitialConfigReducerType,
  treeViewSetSelectionOrderReducerType,
} from './TreeViewConstants';
import { initialState } from './TreeViewReducer';

export const setTaxonomies = (taxonomies) => ({
  type: treeViewSetTaxonomiesReducerType,
  payload: { taxonomies },
});

export const setSelectedTaxonomies = (selectedTaxonomies) => ({
  type: treeViewSetSelectedTaxonomiesReducerType,
  payload: { selectedTaxonomies },
});

export const setSelectedListExpanded = (selectedListExpanded) => ({
  type: treeViewSetSelectedListExpandedReducerType,
  payload: { selectedListExpanded },
});

export const setInitialConfig = (initialConfig) => ({
  type: treeViewSetInitialConfigReducerType,
  payload: { initialConfig },
});

export const setSelectionOrder = (selectionOrder) => ({
  type: treeViewSetSelectionOrderReducerType,
  payload: { selectionOrder },
});

// No module-level counter — the counter lives in Redux state so each
// withReducer instance is isolated and the value participates in time-travel.

export const resetState = () => (dispatch) => {
  dispatch(setTaxonomies(initialState.get('taxonomies')));
  dispatch(setSelectedTaxonomies(initialState.get('selectedTaxonomies')));
  dispatch(setSelectedListExpanded(initialState.get('selectedListExpanded')));
  dispatch(setInitialConfig(initialState.get('initialConfig')));
  dispatch(setSelectionOrder(initialState.get('selectionOrder')));
};

export const toggleTaxonomy = (taxonomy) => (dispatch, getState) => {
  const taxonomies = taxonomiesSelector(getState());

  if (taxonomy.hasHiddenChildren) {
    taxonomy.hasHiddenChildren = false;
  }

  const isExpanded = taxonomy.expanded;
  taxonomy.expanded = !isExpanded;

  if (taxonomy.expanded) {
    each(taxonomy.children, (child) => {
      child.visible = true;
    });
  } else {
    each(taxonomies, (child) => {
      if (child.parents.indexOf(taxonomy) >= 0) {
        if (child.isFolder) {
          child.expanded = false;
        }
        child.visible = false;
      }
    });
  }
  dispatch(setTaxonomies([...taxonomies]));
};

export const loadTaxonomy = (currentTaxonomy) => async (dispatch, getState) => {
  const taxonomies = taxonomiesSelector(getState());
  const initialConfig = initialConfigSelector(getState());
  const { taxonomyType } = initialConfig;

  await TreeView.loadTaxonomyChildren(taxonomyType, currentTaxonomy)
    .then((children) => {
      const indexToInsert = taxonomies.indexOf(currentTaxonomy) + 1;
      taxonomies.splice(indexToInsert, 0, ...children);
      dispatch(setTaxonomies([...taxonomies]));
      return currentTaxonomy;
    })
    .then((taxonomy) => dispatch(toggleTaxonomy(taxonomy)))
    .catch(Logger.error);
};

export const _getRootTaxonomies = (taxonomies) => filter(taxonomies, (taxonomy) => taxonomy.isFolder && taxonomy.nestingLevel === 0);

export const updateSelectedTaxonomies = () => (dispatch, getState) => {
  const taxonomies = taxonomiesSelector(getState());
  const initialConfig = initialConfigSelector(getState());
  const { onChange } = initialConfig;

  const rootTaxonomies = _getRootTaxonomies(taxonomies);

  const { totalHitCount, selectedTaxonomies, selectedRootTaxonomies } = TreeView.getSelectedTaxonomiesData(rootTaxonomies);
  const filteredTaxonomies = TreeView.removeDuplications(selectedTaxonomies);
  const filteredRootTaxonomies = TreeView.removeDuplications(selectedRootTaxonomies);

  // Sort ascending by selectionOrder so that .slice().reverse() in the renderer
  // places the most recently selected item first.
  const taxonomiesCopy = filteredTaxonomies.slice();
  const orderedTaxonomies = taxonomiesCopy.sort((a, b) => {
    const getOrder = (item) => {
      if (item?.selectionOrder != null) return item.selectionOrder;
      if (item?.children?.length === 1) return item?.children[0]?.selectionOrder ?? 0;
      return 0;
    };
    return getOrder(a) - getOrder(b);
  });

  if (isEmpty(orderedTaxonomies)) {
    dispatch(setSelectedListExpanded(false));
  }

  dispatch(setSelectedTaxonomies(orderedTaxonomies));

  if (isFunction(onChange)) {
    onChange({
      totalHitCount,
      selectedTaxonomies: orderedTaxonomies,
      selectedRootTaxonomies: filteredRootTaxonomies,
    });
  }
};

export const removeTaxonomy = (removedTaxonomy, selectedCustomItems) => (dispatch, getState) => {
  const taxonomies = taxonomiesSelector(getState());
  const initialConfig = initialConfigSelector(getState());
  const { onChange } = initialConfig;

  if (removedTaxonomy.custom) {
    selectedCustomItems = reject(
      selectedCustomItems,
      (custom, index) => index === removedTaxonomy.index && custom === removedTaxonomy.value
    );

    if (isFunction(onChange)) {
      onChange({
        selectedCustom: selectedCustomItems,
      });
    }
    return;
  }

  each(taxonomies, (taxonomy) => {
    if (taxonomy.name === removedTaxonomy.name && taxonomy.state === TAXOMONY_STATES.SELECTED) {
      TreeView.selectTaxonomy(taxonomy);
    }
  });
  dispatch(updateSelectedTaxonomies());
};

export const selectTaxonomy = (taxonomy) => (dispatch, getState) => {
  TreeView.selectTaxonomy(taxonomy);
  // Stamp after the toggle so we know the new selected state.
  if (taxonomy.selected) {
    // Read the current counter from Redux state, compute the next value, then
    // persist the final counter so the next call picks up where this one left off.
    // Using Redux state (not a module variable) keeps each withReducer instance
    // isolated and makes the counter participate in time-travel debugging.
    const currentOrder = selectionOrderSelector(getState());
    let nextOrder = currentOrder + 1;
    taxonomy.selectionOrder = nextOrder;

    if (taxonomy.isFolder) {
      taxonomy.children?.forEach((child) => {
        nextOrder += 1;
        child.selectionOrder = nextOrder;
      });
    }

    // Dispatch once with the final counter value after all stamps are applied.
    dispatch(setSelectionOrder(nextOrder));
  } else {
    taxonomy.selectionOrder = null;

    if (taxonomy.isFolder) {
      taxonomy.children?.forEach((child) => {
        child.selectionOrder = null;
      });
    }
  }
  dispatch(updateSelectedTaxonomies());
};

export const checkPreselectedItems = () => (dispatch, getState) => {
  const initialConfig = initialConfigSelector(getState());
  const { selectedItems } = initialConfig;
  const taxonomies = taxonomiesSelector(getState());
  if (!selectedItems) {
    return;
  }

  each(taxonomies, (taxonomy) => {
    if (selectedItems.indexOf(taxonomy.id) >= 0) {
      dispatch(selectTaxonomy(taxonomy));
    }
  });
};

export const initializeTaxonomiesList = () => async (dispatch, getState) => {
  const initialConfig = initialConfigSelector(getState());
  const { options, taxonomyType } = initialConfig;

  dispatch(setSelectedTaxonomies([]));
  dispatch(setSelectedListExpanded(false));

  await TreeView.getTaxonomiesList(taxonomyType)
    .then((taxonomies) => {
      if (!taxonomies.length) {
        if (isFunction(options.onEmptyResult)) {
          options.onEmptyResult();
        }
        return;
      }

      const rootTaxonomies = _getRootTaxonomies(taxonomies);

      each(rootTaxonomies, (taxonomy) => {
        taxonomy.visible = true;
      });

      if (rootTaxonomies.length === 1) {
        rootTaxonomies[0].expanded = true;
        each(rootTaxonomies[0].children, (child) => {
          child.visible = true;
        });
      }
      dispatch(setTaxonomies(taxonomies));
      dispatch(checkPreselectedItems());
      dispatch(updateSelectedTaxonomies());
    })
    .catch((e) => {
      Logger.error(e);
    });
};

export const searchForTaxonomies = (query) => async (dispatch, getState) => {
  if (!query.text) {
    dispatch(initializeTaxonomiesList());
    return;
  }

  const initialConfig = initialConfigSelector(getState());
  const { options, taxonomyType } = initialConfig;

  // eslint-disable-next-line consistent-return
  return TreeView.searchTaxonomyByQuery(taxonomyType, query).then((taxonomies) => {
    if (!taxonomies.length) {
      if (isFunction(options.onEmptyResult)) {
        options.onEmptyResult();
      }
      return taxonomies;
    }

    dispatch(setTaxonomies(taxonomies));
    dispatch(updateSelectedTaxonomies());
    return taxonomies;
  });
};

export const setupFactory = (options, taxonomiesGetter) => {
  TreeView.setEvents(options);
  TreeView.setTaxonomiesGetter(taxonomiesGetter);
};

export const init = (initialConfig) => async (dispatch) => {
  const { options, taxonomiesGetter, initialQuery, onInit, taxonomyType, onChange, selectedItems } = initialConfig;
  if (onInit) {
    onInit({
      search: (query) => dispatch(searchForTaxonomies(query)),
      reset: () => dispatch(initializeTaxonomiesList()),
    });
  }

  dispatch(setInitialConfig({ taxonomyType, initialQuery, taxonomiesGetter, selectedItems, onChange, options }));

  setupFactory(options, taxonomiesGetter);

  if (!initialQuery) {
    dispatch(initializeTaxonomiesList());
    return;
  }

  dispatch(setSelectedListExpanded(false));

  await dispatch(searchForTaxonomies(initialQuery))
    .then((taxonomies) => {
      if (taxonomies && isEmpty(taxonomies)) {
        dispatch(initializeTaxonomiesList());
      }
    })
    .catch((error) => {
      dispatch(initializeTaxonomiesList());
      Logger.error(error);
    });
};
